import { useEffect, useRef } from "react";

const AUTOSAVE_DEBOUNCE_MS = 2000;

/**
 * Safety net for fields that normally save on blur: if a value is left
 * dirty (typed into, never blurred — e.g. the user walks away without
 * clicking out, or switches tabs), this fires `save` a short debounce
 * after typing stops, and immediately when the tab is hidden so a
 * background-tab switch doesn't leave the edit unsaved indefinitely.
 *
 * `draftKey` should be a primitive that changes whenever the draft value
 * changes (e.g. the input string itself, or a `|`-joined composite for a
 * multi-field row) so the debounce timer resets on every keystroke.
 */
export function useAutosaveField(
  draftKey: string | number,
  dirty: boolean,
  save: () => void,
  debounceMs = AUTOSAVE_DEBOUNCE_MS,
): void {
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!dirty) return;
    const timer = window.setTimeout(() => saveRef.current(), debounceMs);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, dirty, debounceMs]);

  useEffect(() => {
    if (!dirty) return;
    const onVisibilityChange = () => {
      if (document.hidden) saveRef.current();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [dirty]);
}
